import * as React from "react";
import {FC, useCallback, useMemo} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {Alert, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle} from "@mui/material";
import Page from "../../../layout/Page";
import PageHeader from "../../../layout/PageHeader";
import PageContent from "../../../layout/PageContent";
import {DateFormat} from "../../../utility/DateFormat";
import {
   EnumVoucherVoucherStrategy,
   FilterFindManyVoucherInput,
   SortFindManyVoucherInput,
   useVouchersPageByIdsLazyQuery,
   useVouchersPageMergeMutation,
   useVouchersPagePaginationLazyQuery,
   useVouchersPageSelectAllLazyQuery,
   useVouchersPageVoucherDeleteMutation,
   useVouchersPageSynchronizeMutation,
   Voucher,
   VouchersPageByIdsQuery,
} from "../../../Queries";
import {VoucherPagePath} from "./VoucherPage";
import {CallMerge, FileDownload, Sync} from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import {Dialogs} from "../../../DialogProvider";
import {SavingUI} from "../../../SavingProvider";
import {useRecoilValue} from "recoil";
import {CurrentUserIsAdmin} from "../../../atoms/CurrentUser";
import {VouchersExport} from "./VouchersExport";
import {ParamBrandHouse} from "../../../Parameters";
import {RangeList} from "../../../components/rangelist/RangeList";
import {RangeCommand, RangeFilter, RangeListColumn, RangeSort, RangeTab} from "../../../components/rangelist/RangeListTypes";

const PAGE_SIZE = 30;

export const VouchersPage: FC<any> = () => {
   const [merging, setMerging] = React.useState<Voucher[]>([]);
   const isAdministrator = useRecoilValue(CurrentUserIsAdmin);
   const [exportIds, setExportIds] = React.useState<string[]>([])

   const {brandHouseId} = useParams<ParamBrandHouse>();
   const navigate = useNavigate();

   const [fetchPagination] = useVouchersPagePaginationLazyQuery();
   const [fetchAllIds] = useVouchersPageSelectAllLazyQuery();
   const [fetchByIds] = useVouchersPageByIdsLazyQuery();
   const [voucherMerge] = useVouchersPageMergeMutation();
   const [voucherDelete] = useVouchersPageVoucherDeleteMutation();
   const [voucherSync] = useVouchersPageSynchronizeMutation();

   const sortSpec: RangeSort<SortFindManyVoucherInput>[] = [{
      id: 'CreatedDesc',
      label: 'Created (newest first)',
      value: SortFindManyVoucherInput._ID_DESC
   }, {
      id: 'CreatedAsc',
      label: 'Created (oldest first)',
      value: SortFindManyVoucherInput._ID_ASC
   }];

   const filterSpec: RangeFilter<FilterFindManyVoucherInput>[] = [{
      id: 'voucherStrategy',
      label: 'Delivery',
      options: [{
         label: 'Receive',
         filter: {
            voucherStrategy: EnumVoucherVoucherStrategy.RECEIVED
         }
      }, {
         label: 'Instant',
         filter: {
            voucherStrategy: EnumVoucherVoucherStrategy.INSTANT
         }
      }, {
         label: 'Handin',
         filter: {
            voucherStrategy: EnumVoucherVoucherStrategy.HANDIN
         }
      }]
   }, {
      id: 'passive',
      label: 'Type',
      options: [{
         label: 'Gift Card',
         filter: {
            passive: true
         }
      }, {
         label: 'Voucher',
         filter: {
            passive: false
         }
      }]
   }, {
      id: 'used',
      label: 'Status',
      options: [{
         label: 'Not used',
         filter: {
            used: null
         }
      }]
   }];

   const tabsSpec: RangeTab[] = useMemo(() => {
      return [{
         id: 'all',
         label: 'All',
         filters: []
      }];
   }, []);

   const columns: RangeListColumn<Voucher>[] = useMemo(() => {
      const cols: RangeListColumn<Voucher>[] = [{
         id: 'date',
         label: 'Date',
         get: (row) => {
            return row.createdAt ? new Date(row.createdAt).getTime() : 0;
         },
         render: (dateTs) => {
            return dateTs ? DateFormat.toPresent(new Date(dateTs)) : '-';
         },
         width: 10,
         ellipsis: true
      }, {
         id: 'consumer',
         label: 'Consumer',
         get: (row) => {
            if (row.consumer) {
               return row.consumer.firstName + ' ' + row.consumer.lastName;
            }
            return 'Unknown';
         },
         width: 15,
         ellipsis: true
      }, {
         id: 'code',
         label: 'Code',
         get: (row) => row.codeAlias || row.code,
         width: 12,
         ellipsis: true
      }, {
         id: 'value',
         label: 'Value',
         get: (row) => Math.round(row.value || 0) + ' ' + row.currency,
         width: 8,
         ellipsis: true
      }, {
         id: 'remaining',
         label: 'Remaining',
         get: (row) => Math.round(row.currentValue || 0) + ' ' + row.currency,
         width: 8,
         ellipsis: true
      }, {
         id: 'deliver',
         label: 'Deliver',
         field: 'voucherStrategy',
         render: (strategy) => {
            if (strategy === 'RECEIVED') {
               return "Receive";
            } else if (strategy === 'INSTANT') {
               return "Instant";
            } else if (strategy === 'HANDIN') {
               return "Handin";
            } else {
               return "Unknown?";
            }
         },
         width: 8,
         ellipsis: true
      }, {
         id: 'active',
         label: 'Active',
         get: (row) => row._id,
         render: (ignore, row) => {
            if (!row) return null;
            if (row.deleted) {
               return <Alert severity={"error"}>{"Deleted " + DateFormat.toReadable(row.deleted)}</Alert>;
            } else if (row.activated) {
               if (row.passive) {
                  if ((row.currentValue || 0) !== row.value) {
                     return <Alert severity={"info"}>Used</Alert>;
                  } else {
                     return <Alert severity={"success"}>Active</Alert>;
                  }
               } else if (row.used && (row.currentValue || 0) > 0) {
                  return <Alert severity={"success"}>Active (see details)</Alert>;
               } else if (row.used) {
                  return <Alert severity={"info"}>{"Used " + DateFormat.toReadable(row.used)}</Alert>;
               } else {
                  return <Alert severity={"success"}>Active</Alert>;
               }
            } else {
               return <Alert severity={"warning"}>Not activated</Alert>;
            }
         },
         width: 12
      }];

      if (isAdministrator) {
         cols.push({
            id: 'type',
            label: '',
            get: (row) => row._id,
            render: (ignore, row) => {
               if (!row) return null;
               if (row.passive) {
                  return <Chip color={"secondary"} label={"GC"}/>;
               } else if (row.newVoucherUsage) {
                  return <Chip color={"primary"} label={"V2"}/>;
               }
               return null;
            },
            width: 3
         });
      }

      return cols;
   }, [isAdministrator]);

   const onRequestData = useCallback(async (search: string | undefined, sort: string | undefined, page: number, perPage: number, filter: any) => {
      const effectiveFilter: FilterFindManyVoucherInput = {
         ...filter,
         brandHouseId: brandHouseId,
         deleted: null
      };

      const {data} = await fetchPagination({
         variables: {
            page: page,
            perPage: perPage,
            search: search,
            sort: sort as SortFindManyVoucherInput || SortFindManyVoucherInput._ID_DESC,
            filter: effectiveFilter
         }
      });

      return {
         data: (data?.VoucherPagination?.items || []) as Voucher[],
         totalCount: data?.VoucherPagination?.count || 0
      };
   }, [fetchPagination, brandHouseId]);

   const onSelectAll = useCallback(async (filter?: FilterFindManyVoucherInput): Promise<string[]> => {
      const effectiveFilter: FilterFindManyVoucherInput = {
         ...filter,
         brandHouseId: brandHouseId,
         deleted: null
      };

      const {data} = await fetchAllIds({
         variables: {
            brandHouseId: brandHouseId!,
            filter: effectiveFilter
         }
      });

      return data?.VoucherPagination?.items?.map(item => item._id) || [];
   }, [fetchAllIds, brandHouseId]);

   const onHandleClick = useCallback((row: Voucher, newWindow?: boolean) => {
      if (newWindow) {
         window.open(VoucherPagePath(brandHouseId!, row.shopId!, row._id), '_blank', '');
      } else {
         navigate(VoucherPagePath(brandHouseId!, row.shopId!, row._id));
      }
   }, [navigate, brandHouseId]);

   const onExecuteMerge = async (row: Voucher) => {
      await voucherMerge({
         variables: {
            shopId: row.shopId!,
            tradeInId: row.tradeInId,
            vouchers: merging.map(merge => String(merge._id))
         }
      });
      setMerging([]);
   };

   const commands: RangeCommand<Voucher>[] = useMemo(() => {
      const cmds: RangeCommand<Voucher>[] = [{
         id: 'merge',
         icon: CallMerge,
         title: 'Merge',
         execute: async (rowIds) => {
            const {data} = await fetchByIds({variables: {voucherIds: rowIds}});
            const vouchers = (data?.VoucherByIds || []) as VouchersPageByIdsQuery['VoucherByIds'];
            setMerging(vouchers as Voucher[]);
         }
      }, {
         id: 'export',
         title: 'Export to excel',
         icon: FileDownload,
         execute: async (rowIds) => {
            setExportIds(rowIds);
         }
      }, {
         id: 'synchronize',
         title: 'Synchronize',
         icon: Sync,
         execute: async (rowIds) => {
            const {data} = await fetchByIds({variables: {voucherIds: rowIds}});
            const vouchers = data?.VoucherByIds || [];
            await SavingUI.process(async () => {
               for (const voucher of vouchers) {
                  if (voucher?.shopId && voucher?.code) {
                     await voucherSync({
                        variables: {
                           shopId: voucher.shopId,
                           code: voucher.code
                        }
                     });
                  }
               }
            }, 'Synchronized');
         }
      }];

      if (isAdministrator) {
         cmds.push({
            id: 'delete',
            icon: DeleteIcon,
            title: 'Delete',
            execute: async (rowIds) => {
               Dialogs.confirm({
                  title: 'Really delete vouchers?',
                  subtitle: 'Its not possible to undo this operation'
               }, async () => {
                  const {data} = await fetchByIds({variables: {voucherIds: rowIds}});
                  const vouchers = data?.VoucherByIds || [];
                  await SavingUI.process(async () => {
                     for (const voucher of vouchers) {
                        if (voucher?.shopId) {
                           await voucherDelete({
                              variables: {
                                 shopId: voucher.shopId,
                                 voucherId: voucher._id
                              }
                           });
                        }
                     }
                  }, 'Deletion completed');
               });
            }
         });
      }

      return cmds;
   }, [isAdministrator, voucherDelete, voucherSync, fetchByIds]);

   return <Page>
      {merging.length > 0 && <Dialog open={merging.length > 0}>
         <DialogTitle>This will move the remaining value from selected vouchers</DialogTitle>
         <DialogContent>
            The value of the selected vouchers will be moved into one voucher.
            Please select the main voucher from the list below.
         </DialogContent>
         <DialogContent>
            {merging.map(merge => {
               return <Button key={merge._id} onClick={() => onExecuteMerge(merge)}>{merge.code}</Button>;
            })}
         </DialogContent>
         <DialogActions>
            <Button variant={"text"} onClick={() => setMerging([])}>Hide</Button>
         </DialogActions>
      </Dialog>}
      <PageHeader title={"Rewards"}/>
      <PageContent fullHeight>
         <RangeList
            globalId={"brandVouchers"}
            columns={columns}
            filters={filterSpec}
            sorts={sortSpec}
            tabs={tabsSpec}
            itemsPerPage={PAGE_SIZE}
            selections={true}
            commands={commands}
            onSelectRow={onHandleClick}
            onRequestData={onRequestData}
            onSelectAll={onSelectAll}
            searchLabel="Search vouchers..."
            options={{
               itemHeight: 55
            }}
         />
         {exportIds.length > 0 && <VouchersExport ids={exportIds}/>}
      </PageContent>
   </Page>;
};
